import { useTitles } from "@/hooks/graphql/useTitles";
import type { RacingHit } from "@/features/search/components/Results/models/racingHit";
import { TitleIcon } from "@/features/shell/components/shared/TitleIcon";
import * as styles from "@/features/search/components/Results/Results.module.scss";
import React, { useMemo, useRef } from "react";
import { useConfigure } from "react-instantsearch";
import { useHasPermission } from "@/features/auth/hooks/usePermission";
import { ResultsInIndex } from "./ResultsInIndex/ResultsInIndex";
import { Row } from "./Row/Row";
import { Highlight } from "./Highlight/Highlight";
import { NextHighlight } from "./Highlight/NextHighlight";
import type { HitAttributeHighlightResult } from "./Highlight/types";
import { useNormalisedHits } from "@/features/search/components/Results/hooks/useNormalisedHits";

const getMatchPercentage = (h?: HitAttributeHighlightResult) => {
  if (!h || !h.matchedWords?.length) return -1;

  if (h.matchLevel === "full") return 1000;

  return h.matchedWords.join("").length;
};

const findBestMatchIndex = (
  competitors?: HitAttributeHighlightResult[],
  jockeys?: HitAttributeHighlightResult[],
  trainers?: HitAttributeHighlightResult[],
): number => {
  const maxLength = Math.max(
    competitors?.length ?? 0,
    jockeys?.length ?? 0,
    trainers?.length ?? 0,
  );

  const priorities = Array.from({ length: maxLength }, (_, i) =>
    Math.max(
      getMatchPercentage(competitors?.[i]),
      getMatchPercentage(jockeys?.[i]),
      getMatchPercentage(trainers?.[i]),
    ),
  );

  return priorities.findIndex((p) => p === Math.max(...priorities));
};

export const RacingResults = () => {
  const titles = useTitles();
  const { items, results } = useNormalisedHits<RacingHit>();
  const hasMatchedOnRunner = useRef(false);

  // Restrict to domestic (ANZ) races unless user can bet international racing
  const canIntlRacing = useHasPermission("submitInternationalRacingBet");
  const filters = useMemo(
    () => (canIntlRacing ? "" : "region:ANZ"),
    [canIntlRacing],
  );

  useConfigure({ filters });

  return (
    <ResultsInIndex
      indexName={`Racing`}
      total={results?.nbHits || 0}
      pages={results?.nbPages || 0}
      perPage={results?.hitsPerPage || 0}
    >
      {(items || []).map((item) => {
        const venueHighlight = item.normalisedHighlights?.venue?.[0];

        const titleHighlightPath = "venue.0";

        // default second highlight to event name
        let secondaryHighlightPath = "event.0";
        let secondaryHighlightText = item.event;
        let secondaryHighlight = item.normalisedHighlights?.event?.[0];

        const additionalHighlights = [];
        let ignorePaths = [titleHighlightPath, secondaryHighlightPath];

        if (
          ["HORSE_RACING", "HARNESS_RACING", "GREYHOUNDS"].includes(item.sport)
        ) {
          // we want to replace the second highlight with the competitor instead of the event name if its matched competitor, jockey or trainer on the search terms
          const matchIndex = findBestMatchIndex(
            item.normalisedHighlights?.competitors,
            item.normalisedHighlights?.jockeys,
            item.normalisedHighlights?.trainers,
          );

          if (matchIndex !== -1) {
            hasMatchedOnRunner.current = true;

            secondaryHighlightPath = `competitors.${matchIndex}`;
            secondaryHighlightText = item.competitors[matchIndex];
            secondaryHighlight =
              item.normalisedHighlights?.competitors?.[matchIndex];

            ignorePaths = [titleHighlightPath, secondaryHighlightPath];

            // we want to also show the jockey/driver and trainer if applicable
            if (item.jockeys[matchIndex]) {
              // Ignore all jockeys in the next highlight (down further)
              // don't just ignore the matched jockey.index, there may be a partial match on another runners jockey
              ignorePaths.push(`jockeys`);
              const title = item.sport === "HARNESS_RACING" ? "D: " : "J: ";
              additionalHighlights.push(
                <div
                  key={`additional-highlight-jockey`}
                  className={styles.additionalHighlightRow}
                >
                  {title}
                  <Highlight
                    text={item.jockeys[matchIndex]}
                    matchedWords={
                      item.normalisedHighlights?.jockeys?.[matchIndex]
                        ?.matchedWords || []
                    }
                    html={
                      item.normalisedHighlights?.jockeys?.[matchIndex]?.value ||
                      ""
                    }
                    className={styles.displayInlineBlock}
                  />
                </div>,
              );
            }

            if (item.trainers[matchIndex]) {
              // Ignore all trainers in the next highlight (down further)
              // don't just ignore the matched trainer.index, there may be 2 runners with the same trainer
              ignorePaths.push(`trainers`);
              additionalHighlights.push(
                <div
                  key={`additional-highlight-trainer`}
                  className={styles.additionalHighlightRow}
                >
                  T:{" "}
                  <Highlight
                    text={item.trainers[matchIndex]}
                    matchedWords={
                      item.normalisedHighlights?.trainers?.[matchIndex]
                        ?.matchedWords || []
                    }
                    html={
                      item.normalisedHighlights?.trainers?.[matchIndex]
                        ?.value || ""
                    }
                    className={styles.displayInlineBlock}
                  />
                </div>,
              );
            }
          }
        }

        return (
          <Row
            key={item.objectID}
            eventId={item.objectID}
            icon={<TitleIcon title={titles[item.sport?.toLowerCase()]} />}
            title={
              <>
                <div className={styles.raceNumber}>R{item.raceNumber}</div>
                <Highlight
                  text={item.venue}
                  matchedWords={venueHighlight?.matchedWords || []}
                  html={venueHighlight?.value || ""}
                />
              </>
            }
            subTitle={
              <>
                <div>
                  <Highlight
                    text={secondaryHighlightText}
                    matchedWords={secondaryHighlight?.matchedWords || []}
                    html={secondaryHighlight?.value || ""}
                    convertToTitleCase
                  />
                </div>
                <div>{additionalHighlights}</div>
              </>
            }
            to={`/racing/betting/race/${item.meetingId}/${item.objectID}`}
            startTime={item.scheduledStartTime}
            {...(!hasMatchedOnRunner.current && {
              highlight: (
                <NextHighlight
                  highlights={item.normalisedHighlights}
                  ignore={ignorePaths}
                  sport={item.sport}
                />
              ),
            })}
          />
        );
      })}
    </ResultsInIndex>
  );
};
